import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Phone } from 'lucide-react';
import { formatarMoedaPlano } from '@/constants/planosCNPJ';
import { formatarTelefone } from '@/utils/formatters';
import { cn } from '@/lib/utils';
import type { LeadCarteira } from '@/hooks/useLeadsCarteira';

interface PlanoAtualTTProps {
  lead: LeadCarteira;
  proposta: {
    gb_atual: string;
    fibra_atual: string;
    tv_atual: string;
    linhas_atual?: string;
    valor_atual?: string;
    obs_atual?: string;
  };
  onPropostaChange: (field: string, value: string) => void;
  qtdLinhasAtuais: number;
  ocultarPlanoAtual?: boolean;
  onToggleOcultar?: () => void;
}

const parsarValorMonetario = (valor: string | number | undefined): number => {
  if (typeof valor === 'number') return valor;
  if (!valor) return 0;
  const str = String(valor).replace(/[R$\s]/g, '').trim();
  const temPonto = str.includes('.');
  const temVirgula = str.includes(',');
  if (temPonto && !temVirgula) {
    // Formato americano: "716.44" → ponto é decimal
    return parseFloat(str) || 0;
  }
  // Formato brasileiro: "1.234,56" → remove pontos, converte vírgula
  return parseFloat(str.replace(/\./g, '').replace(',', '.')) || 0;
};

const ROTULO = 'text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground';

// tile do molde: 64px, número 20px Bricolage; `—` faint quando não medido
function TilePlano({ rotulo, valor, sub }: { rotulo: string; valor: string | null; sub?: string }) {
  return (
    <div className="rounded-[9px] border border-border bg-card px-3 py-2">
      <p className={ROTULO}>{rotulo}</p>
      <p className={cn('truncate font-display text-[20px] font-bold leading-tight tabular-nums', valor == null ? 'text-muted-foreground/40' : 'text-foreground')}>
        {valor ?? '—'}
      </p>
      {sub && <p className="truncate text-[9.5px] text-muted-foreground/80">{sub}</p>}
    </div>
  );
}

export function PlanoAtualTT({ lead, proposta, onPropostaChange, qtdLinhasAtuais, ocultarPlanoAtual, onToggleOcultar }: PlanoAtualTTProps) {
  const linhasNum = parseInt(proposta.linhas_atual || '') || qtdLinhasAtuais;
  const valorNum = proposta.valor_atual
    ? (parseFloat(proposta.valor_atual.replace(',', '.')) || 0)
    : (lead.valor_fatura_atual
      || parsarValorMonetario(lead.dados_discadora?.valor_combo)
      || parsarValorMonetario(lead.dados_discadora?.valor_movel)
      || 0);
  const linhasCpf = [1, 2, 3, 4, 5, 6]
    .map((i) => (lead.dados_discadora as Record<string, unknown> | null)?.[`movel_${i}`])
    .filter(Boolean) as string[];

  return (
    <section className={cn('overflow-hidden rounded-xl border border-border bg-card', ocultarPlanoAtual && 'opacity-60')}>
      <div className="flex min-h-[34px] items-center gap-2 border-b bg-[#FBFAF9] px-3.5 py-1.5 dark:bg-white/[0.02]">
        <h3 className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-card-foreground">
          <Phone className="h-3 w-3 text-muted-foreground" /> Plano atual · Pessoa Física
        </h3>
        <span className="inline-flex items-center rounded-full border border-border bg-[#F2EEE8] px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-muted-foreground dark:bg-white/[0.06]">
          {lead.dados_discadora?.tipo_cliente || 'MÓVEL'}
        </span>
        <span className="min-w-0 flex-1" />
        {onToggleOcultar && (
          <button
            type="button"
            className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:text-foreground"
            onClick={onToggleOcultar}
            title={ocultarPlanoAtual ? 'Plano atual OCULTO na proposta — clique para mostrar' : 'Clique para OCULTAR o plano atual da proposta'}
          >
            {ocultarPlanoAtual ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>
        )}
      </div>
      <div className="space-y-3 px-3.5 py-2.5">
        {/* Resumo da IA — exibido quando o webhook bee-fechamento-banda-larga preenche obs_atual */}
        {proposta.obs_atual && (
          <div className="relative overflow-hidden rounded-r-lg rounded-l-none border border-border bg-[#FBFAF9] pl-1 dark:bg-white/[0.02]">
            <span aria-hidden className="absolute inset-y-0 left-0 w-1 bg-[linear-gradient(180deg,#6E1626,#4E1522_55%,#3E0E1B)]" />
            <div className="px-3 py-2">
              <p className="text-[9.5px] font-bold uppercase tracking-[0.08em] text-primary dark:text-[#C87484]">Resumo da IA</p>
              <p className="mt-0.5 whitespace-pre-wrap text-xs leading-relaxed text-[#5A4848] dark:text-[#B5A8A8]">{proposta.obs_atual}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-2">
          <TilePlano rotulo="Linhas" valor={linhasNum > 0 ? String(linhasNum) : null} sub="móveis" />
          <TilePlano rotulo="Valor total" valor={valorNum > 0 ? formatarMoedaPlano(valorNum) : null} sub="/mês" />
          <TilePlano rotulo="Fatura" valor={lead.dados_discadora?.data_fatura || null} />
        </div>

        {linhasCpf.length > 0 && (
          <div>
            <p className={cn(ROTULO, 'mb-1.5')}>Linhas do CPF</p>
            <div className="flex flex-wrap gap-1.5">
              {linhasCpf.map((tel, i) => (
                <span key={`${tel}-${i}`} className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 text-[10.5px] font-semibold tabular-nums">
                  <span className="text-muted-foreground/60">{i + 1}.</span> {formatarTelefone(String(tel))}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="border-t pt-2.5">
          <p className={cn(ROTULO, 'mb-2')}>Preencher após contato com cliente</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            <div className="min-w-0">
              <Label className={ROTULO}>Linhas</Label>
              <Input
                type="number"
                min="1"
                value={proposta.linhas_atual || qtdLinhasAtuais.toString()}
                onChange={(e) => onPropostaChange('linhas_atual', e.target.value)}
                placeholder="Ex: 2"
                className="mt-1 h-[30px] text-xs"
              />
            </div>
            <div className="min-w-0">
              <Label className={ROTULO}>GB atual</Label>
              <Input
                type="number"
                value={proposta.gb_atual}
                onChange={(e) => onPropostaChange('gb_atual', e.target.value)}
                placeholder="Ex: 50"
                className="mt-1 h-[30px] text-xs"
              />
            </div>
            <div className="min-w-0">
              <Label className={ROTULO}>Valor total</Label>
              <Input
                type="text"
                value={proposta.valor_atual || ''}
                onChange={(e) => onPropostaChange('valor_atual', e.target.value)}
                placeholder="Ex: 250,00"
                className="mt-1 h-[30px] text-xs"
              />
            </div>
            <div className="min-w-0">
              <Label className={ROTULO}>Tem fibra?</Label>
              <Input
                value={proposta.fibra_atual}
                onChange={(e) => onPropostaChange('fibra_atual', e.target.value)}
                placeholder="Não / 400MB..."
                className="mt-1 h-[30px] text-xs"
              />
            </div>
            <div className="min-w-0">
              <Label className={ROTULO}>Tem TV?</Label>
              <Input
                value={proposta.tv_atual || ''}
                onChange={(e) => onPropostaChange('tv_atual', e.target.value)}
                placeholder="Não / Compacto..."
                className="mt-1 h-[30px] text-xs"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
